import React, { useState, useEffect, useRef, Fragment } from "react";
import { Dialog, Transition, Listbox, ListboxButton, ListboxOption, ListboxOptions, Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption } from "@headlessui/react";
import { XMarkIcon, CheckIcon, ChevronUpDownIcon } from "@heroicons/react/24/outline";
import petition_user from "../../../../petitions/petition_user";
import petition_post from "../../../../petitions/petition_post";
import { searchGlobal } from "../../../../../../services/globalSearchService";

// Color the account's status so closed / cancelled / duplicate accounts stand
// out at a glance in the results list — that's the whole point of the search.
function statusPillClasses(status) {
  const s = (status || "").toLowerCase();
  if (["active", "enabled", "open"].includes(s)) {
    return "tw-bg-green-100 tw-text-green-700 dark:tw-bg-green-900/50 dark:tw-text-green-300";
  }
  if (["closed", "cancelled", "canceled", "disabled", "inactive", "terminated"].includes(s)) {
    return "tw-bg-red-100 tw-text-red-700 dark:tw-bg-red-900/50 dark:tw-text-red-300";
  }
  return "tw-bg-gray-100 tw-text-gray-600 dark:tw-bg-gray-700 dark:tw-text-gray-300";
}

const priorities = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const statuses = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "pending", label: "Pending Customer" },
];

export default function NewTicketModal({ show, onHide, onSuccess, accountId }) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [services, setServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState("");
  const [contactSearchQuery, setContactSearchQuery] = useState("");
  // The full selected account object, kept alongside its id so the input can
  // display its name even after the results list has been replaced by a new
  // search (results only ever hold the latest query's matches).
  const [selectedAccount, setSelectedAccount] = useState(null);
  const accountSearchDebounce = useRef(null);

  // Form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState(priorities[1]); // Default to Medium
  const [status, setStatus] = useState(statuses[0]); // Default to New
  const [assignedUserId, setAssignedUserId] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedContactId, setSelectedContactId] = useState("");
  const [dueDate, setDueDate] = useState("");

  useEffect(() => {
    if (show) {
      loadUsers();
      if (accountId) {
        // If accountId is provided, load its services and contacts
        loadServicesForAccount(accountId);
        loadContactsForAccount(accountId);
      }
    } else {
      // Reset form when modal closes
      resetForm();
      setAccounts([]);
      setAccountSearchQuery("");
      setSelectedAccount(null);
      if (accountSearchDebounce.current) clearTimeout(accountSearchDebounce.current);
    }
  }, [show, accountId]);

  const loadUsers = () => {
    petition_user("users", "?unlimit=true")
      .then(({ data: { result } }) => {
        setUsers(result.model_list || []);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // Server-side account search, mirroring the app's global search: multi-term
  // (spaces), matches name / account number / phone, and — crucially — returns
  // closed & duplicate accounts so they can actually be found here.
  const runAccountSearch = (query) => {
    const q = (query || "").trim();
    if (!q) {
      setAccounts([]);
      setLoadingAccounts(false);
      return;
    }
    setLoadingAccounts(true);
    searchGlobal(q, 20)
      .then(({ accounts: results }) => {
        setAccounts(results || []);
        setLoadingAccounts(false);
      })
      .catch((error) => {
        console.log(error);
        setAccounts([]);
        setLoadingAccounts(false);
      });
  };

  const handleAccountQueryChange = (value) => {
    setAccountSearchQuery(value);
    if (accountSearchDebounce.current) clearTimeout(accountSearchDebounce.current);
    if (!value.trim()) {
      setAccounts([]);
      setLoadingAccounts(false);
      return;
    }
    setLoadingAccounts(true);
    accountSearchDebounce.current = setTimeout(() => runAccountSearch(value), 300);
  };

  const loadServicesForAccount = (accountId) => {
    if (!accountId) {
      setServices([]);
      setSelectedServiceId("");
      return;
    }

    setLoadingServices(true);
    petition_user("servicesAll", `?account_id=${accountId}&status=active`)
      .then(({ data: { result } }) => {
        setServices(result.services || result.model_list || []);
        setLoadingServices(false);
      })
      .catch((error) => {
        console.log(error);
        setLoadingServices(false);
        setServices([]);
      });
  };

  const loadContactsForAccount = (accountId) => {
    if (!accountId) {
      setContacts([]);
      setSelectedContactId("");
      return;
    }

    setLoadingContacts(true);
    petition_user("contacts-search", accountId, "&unlimit=true")
      .then(({ data: { result } }) => {
        setContacts(result.model_list || result || []);
        setLoadingContacts(false);
      })
      .catch((error) => {
        console.log(error);
        setLoadingContacts(false);
        setContacts([]);
      });
  };

  useEffect(() => {
    // When account changes, load its services and contacts
    if (selectedAccountId) {
      loadServicesForAccount(selectedAccountId);
      loadContactsForAccount(selectedAccountId);
    } else {
      setServices([]);
      setSelectedServiceId("");
      setContacts([]);
      setSelectedContactId("");
      setContactSearchQuery("");
    }
  }, [selectedAccountId]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setPriority(priorities[1]);
    setStatus(statuses[0]);
    setAssignedUserId("");
    setSelectedAccountId("");
    setSelectedAccount(null);
    setSelectedServiceId("");
    setSelectedContactId("");
    setContactSearchQuery("");
    setServices([]);
    setContacts([]);
    setDueDate("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const ticketData = {
      title,
      description,
      priority: priority.value,
      status: status.value,
      assigned_tech_id: assignedUserId || null,
      account_id: accountId || parseInt(selectedAccountId) || null,
      account_service_id: selectedServiceId ? parseInt(selectedServiceId) : null,
      reporter_contact_id: selectedContactId ? parseInt(selectedContactId) : null,
      due_date: dueDate || null,
    };

    petition_post("tickets", ticketData)
      .then(({ data }) => {
        setLoading(false);
        resetForm();
        onHide();
        if (onSuccess) onSuccess(data);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  return (
    <Transition show={show} as={Fragment}>
      <Dialog onClose={onHide} className="tw-relative tw-z-50">
        <Transition.Child
          as={Fragment}
          enter="tw-ease-out tw-duration-300"
          enterFrom="tw-opacity-0"
          enterTo="tw-opacity-100"
          leave="tw-ease-in tw-duration-200"
          leaveFrom="tw-opacity-100"
          leaveTo="tw-opacity-0"
        >
          <div className="tw-fixed tw-inset-0 tw-bg-black/30" />
        </Transition.Child>

        <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4">
          <Transition.Child
            as={Fragment}
            enter="tw-ease-out tw-duration-300"
            enterFrom="tw-opacity-0 tw-scale-95"
            enterTo="tw-opacity-100 tw-scale-100"
            leave="tw-ease-in tw-duration-200"
            leaveFrom="tw-opacity-100 tw-scale-100"
            leaveTo="tw-opacity-0 tw-scale-95"
          >
            <Dialog.Panel className="tw-w-full tw-max-w-2xl tw-max-h-[90vh] tw-overflow-y-auto tw-rounded-2xl tw-bg-white dark:tw-bg-gray-800 tw-shadow-xl">
              {/* Header */}
              <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200 dark:tw-border-gray-700">
                <Dialog.Title className="tw-text-xl tw-font-bold tw-text-gray-900 dark:tw-text-white">
                  Create New Ticket
                </Dialog.Title>
                <button
                  onClick={onHide}
                  className="tw-p-2 tw-rounded-lg tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"
                >
                  <XMarkIcon className="tw-w-5 tw-h-5" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="tw-p-6 tw-space-y-5">
                {/* Title */}
                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                    Title <span className="tw-text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Brief description of the issue"
                    className="tw-w-full tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-4 tw-py-3 tw-text-base dark:tw-text-white focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Detailed description of the issue..."
                    className="tw-w-full tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-4 tw-py-3 tw-text-base dark:tw-text-white focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20 tw-resize-none"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                {/* Priority & Status */}
                <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                  {/* Priority */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Priority
                    </label>
                    <Listbox value={priority} onChange={setPriority}>
                      <div className="tw-relative">
                        <ListboxButton className="tw-relative tw-w-full tw-cursor-pointer tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-py-3 tw-pl-4 tw-pr-10 tw-text-left tw-text-base dark:tw-text-white focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20">
                          <span className="tw-block tw-truncate">{priority.label}</span>
                          <span className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3">
                            <ChevronUpDownIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                          </span>
                        </ListboxButton>
                        <ListboxOptions className="tw-absolute tw-z-10 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-xl tw-bg-white dark:tw-bg-gray-800 tw-py-1 tw-shadow-lg tw-ring-1 tw-ring-black/5 focus:tw-outline-none">
                          {priorities.map((p) => (
                            <ListboxOption
                              key={p.value}
                              value={p}
                              className="tw-relative tw-cursor-pointer tw-select-none tw-py-2 tw-pl-10 tw-pr-4 tw-text-gray-900 dark:tw-text-white data-[focus]:tw-bg-blue-100 dark:data-[focus]:tw-bg-blue-900/50"
                            >
                              {({ selected }) => (
                                <>
                                  <span className={`tw-block tw-truncate ${selected ? 'tw-font-medium' : 'tw-font-normal'}`}>
                                    {p.label}
                                  </span>
                                  {selected && (
                                    <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-blue-600">
                                      <CheckIcon className="tw-h-5 tw-w-5" />
                                    </span>
                                  )}
                                </>
                              )}
                            </ListboxOption>
                          ))}
                        </ListboxOptions>
                      </div>
                    </Listbox>
                  </div>

                  {/* Status */}
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Status
                    </label>
                    <Listbox value={status} onChange={setStatus}>
                      <div className="tw-relative">
                        <ListboxButton className="tw-relative tw-w-full tw-cursor-pointer tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-py-3 tw-pl-4 tw-pr-10 tw-text-left tw-text-base dark:tw-text-white focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20">
                          <span className="tw-block tw-truncate">{status.label}</span>
                          <span className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3">
                            <ChevronUpDownIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                          </span>
                        </ListboxButton>
                        <ListboxOptions className="tw-absolute tw-z-10 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-xl tw-bg-white dark:tw-bg-gray-800 tw-py-1 tw-shadow-lg tw-ring-1 tw-ring-black/5 focus:tw-outline-none">
                          {statuses.map((s) => (
                            <ListboxOption
                              key={s.value}
                              value={s}
                              className="tw-relative tw-cursor-pointer tw-select-none tw-py-2 tw-pl-10 tw-pr-4 tw-text-gray-900 dark:tw-text-white data-[focus]:tw-bg-blue-100 dark:data-[focus]:tw-bg-blue-900/50"
                            >
                              {({ selected }) => (
                                <>
                                  <span className={`tw-block tw-truncate ${selected ? 'tw-font-medium' : 'tw-font-normal'}`}>
                                    {s.label}
                                  </span>
                                  {selected && (
                                    <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-blue-600">
                                      <CheckIcon className="tw-h-5 tw-w-5" />
                                    </span>
                                  )}
                                </>
                              )}
                            </ListboxOption>
                          ))}
                        </ListboxOptions>
                      </div>
                    </Listbox>
                  </div>
                </div>

                {/* Account (if not passed as prop) */}
                {!accountId && (
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Account
                    </label>
                    <Combobox
                      value={selectedAccount}
                      onChange={(account) => {
                        setSelectedAccount(account);
                        setSelectedAccountId(account?.pk ? String(account.pk) : "");
                      }}
                    >
                      <div className="tw-relative">
                        <ComboboxButton as="div" className="tw-w-full">
                          <ComboboxInput
                            className="tw-w-full tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-4 tw-py-3 tw-pr-10 tw-text-base dark:tw-text-white tw-placeholder-gray-400 focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20"
                            displayValue={(account) =>
                              account ? (account.name || account.company_name || `Account #${account.pk}`) : ''
                            }
                            onChange={(e) => handleAccountQueryChange(e.target.value)}
                            placeholder="Search by name, account #, or phone…"
                          />
                        </ComboboxButton>
                        <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3 tw-pointer-events-none">
                          {loadingAccounts ? (
                            <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-text-blue-500" viewBox="0 0 24 24">
                              <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <ChevronUpDownIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                          )}
                        </div>
                        <ComboboxOptions className="tw-absolute tw-z-50 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-xl tw-bg-white dark:tw-bg-gray-800 tw-py-1 tw-text-base tw-shadow-lg tw-ring-1 tw-ring-black/5 dark:tw-ring-white/10 focus:tw-outline-none">
                          {(() => {
                            const query = accountSearchQuery.trim();

                            if (loadingAccounts) {
                              return (
                                <div className="tw-relative tw-cursor-default tw-select-none tw-px-4 tw-py-3 tw-text-gray-500 dark:tw-text-gray-400">
                                  Searching accounts…
                                </div>
                              );
                            }

                            if (!query) {
                              return (
                                <div className="tw-relative tw-cursor-default tw-select-none tw-px-4 tw-py-3 tw-text-gray-500 dark:tw-text-gray-400">
                                  Start typing to search accounts (includes closed).
                                </div>
                              );
                            }

                            if (accounts.length === 0) {
                              return (
                                <div className="tw-relative tw-cursor-default tw-select-none tw-px-4 tw-py-3 tw-text-gray-500 dark:tw-text-gray-400">
                                  No accounts found for “{query}”
                                </div>
                              );
                            }

                            return accounts.map((account) => {
                              const meta = [];
                              if (account.accountnumber) meta.push(`#${account.accountnumber}`);
                              if (account.phone) meta.push(account.phone);
                              return (
                                <ComboboxOption
                                  key={account.pk}
                                  value={account}
                                  className="tw-relative tw-cursor-pointer tw-select-none tw-py-3 tw-pl-10 tw-pr-4 tw-text-gray-900 dark:tw-text-white data-[focus]:tw-bg-blue-50 dark:data-[focus]:tw-bg-blue-900/50"
                                >
                                  {({ selected }) => (
                                    <>
                                      <div className="tw-flex tw-items-center tw-gap-2">
                                        <span className={`tw-truncate ${selected ? 'tw-font-medium' : 'tw-font-normal'}`}>
                                          {account.name || account.company_name || `Account #${account.pk}`}
                                        </span>
                                        {account.status && (
                                          <span className={`tw-flex-shrink-0 tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-capitalize ${statusPillClasses(account.status)}`}>
                                            {account.status}
                                          </span>
                                        )}
                                      </div>
                                      {meta.length > 0 && (
                                        <div className="tw-mt-0.5 tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-truncate">
                                          {meta.join(" · ")}
                                        </div>
                                      )}
                                      {selected && (
                                        <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-blue-600 dark:tw-text-blue-400">
                                          <CheckIcon className="tw-h-5 tw-w-5" />
                                        </span>
                                      )}
                                    </>
                                  )}
                                </ComboboxOption>
                              );
                            });
                          })()}
                        </ComboboxOptions>
                      </div>
                    </Combobox>
                  </div>
                )}

                {/* Service Selection (shown when account is selected) */}
                {(accountId || selectedAccountId) && (
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Service (for SLA tracking)
                    </label>
                    <div className="tw-relative">
                      <select
                        className="tw-w-full tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-4 tw-py-3 tw-pr-10 tw-text-base dark:tw-text-white focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20 disabled:tw-opacity-50"
                        value={selectedServiceId}
                        onChange={(e) => setSelectedServiceId(e.target.value)}
                        disabled={loadingServices}
                      >
                        {loadingServices ? (
                          <option value="">Loading services...</option>
                        ) : (
                          <>
                            <option value="">No service (no SLA tracking)</option>
                            {services.map((service) => (
                              <option key={service.pk} value={service.pk}>
                                {service.product?.name || service.prod_description || `Service #${service.pk}`}
                                {(service.response_time_sla_minutes || service.resolution_time_sla_minutes) &&
                                  ` - SLA: ${service.response_time_sla_minutes ? `${service.response_time_sla_minutes}min response` : ''}`}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                      {loadingServices && (
                        <div className="tw-absolute tw-right-3 tw-top-1/2 tw--translate-y-1/2 tw-pointer-events-none">
                          <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-text-blue-500" viewBox="0 0 24 24">
                            <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Contact Selection (shown when account is selected) */}
                {(accountId || selectedAccountId) && (
                  <div>
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                      Contact
                    </label>
                    <Combobox value={selectedContactId} onChange={(val) => { setSelectedContactId(val || ""); setContactSearchQuery(""); }} nullable>
                      <div className="tw-relative">
                        <ComboboxButton as="div" className="tw-w-full">
                          <ComboboxInput
                            className="tw-w-full tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-4 tw-py-3 tw-pr-10 tw-text-base dark:tw-text-white tw-placeholder-gray-400 focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20 disabled:tw-opacity-50"
                            displayValue={(contactId) => {
                              const contact = contacts.find(c => c.pk === contactId);
                              if (!contact) return '';
                              const name = [contact.firstname, contact.lastname].filter(Boolean).join(" ");
                              return name || contact.email || `Contact #${contact.pk}`;
                            }}
                            onChange={(e) => setContactSearchQuery(e.target.value)}
                            placeholder={loadingContacts ? "Loading contacts..." : "Search contacts..."}
                            disabled={loadingContacts}
                          />
                        </ComboboxButton>
                        <div className="tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-items-center tw-pr-3 tw-pointer-events-none">
                          {loadingContacts ? (
                            <svg className="tw-animate-spin tw-h-5 tw-w-5 tw-text-blue-500" viewBox="0 0 24 24">
                              <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <ChevronUpDownIcon className="tw-h-5 tw-w-5 tw-text-gray-400" />
                          )}
                        </div>
                        <ComboboxOptions className="tw-absolute tw-z-50 tw-mt-1 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-xl tw-bg-white dark:tw-bg-gray-800 tw-py-1 tw-text-base tw-shadow-lg tw-ring-1 tw-ring-black/5 dark:tw-ring-white/10 focus:tw-outline-none">
                          {(() => {
                            const query = contactSearchQuery.toLowerCase().trim();
                            const filtered = query
                              ? contacts.filter(c => {
                                  const name = [c.firstname, c.lastname].filter(Boolean).join(" ").toLowerCase();
                                  const email = (c.email || "").toLowerCase();
                                  return name.includes(query) || email.includes(query);
                                })
                              : contacts;

                            if (filtered.length === 0) {
                              return (
                                <div className="tw-relative tw-cursor-default tw-select-none tw-px-4 tw-py-3 tw-text-gray-500 dark:tw-text-gray-400">
                                  No contacts found
                                </div>
                              );
                            }

                            return filtered.map((contact) => (
                              <ComboboxOption
                                key={contact.pk}
                                value={contact.pk}
                                className="tw-relative tw-cursor-pointer tw-select-none tw-py-3 tw-pl-10 tw-pr-4 tw-text-gray-900 dark:tw-text-white data-[focus]:tw-bg-blue-50 dark:data-[focus]:tw-bg-blue-900/50"
                              >
                                {({ selected }) => (
                                  <>
                                    <div>
                                      <span className={`tw-block tw-truncate ${selected ? 'tw-font-medium' : 'tw-font-normal'}`}>
                                        {[contact.firstname, contact.lastname].filter(Boolean).join(" ") || `Contact #${contact.pk}`}
                                      </span>
                                      {contact.email && (
                                        <span className="tw-block tw-truncate tw-text-sm tw-text-gray-500 dark:tw-text-gray-400">
                                          {contact.email}
                                        </span>
                                      )}
                                    </div>
                                    {selected && (
                                      <span className="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-blue-600 dark:tw-text-blue-400">
                                        <CheckIcon className="tw-h-5 tw-w-5" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </ComboboxOption>
                            ));
                          })()}
                        </ComboboxOptions>
                      </div>
                    </Combobox>
                  </div>
                )}

                {/* Assigned User */}
                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                    Assign To
                  </label>
                  <select
                    className="tw-w-full tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-4 tw-py-3 tw-text-base dark:tw-text-white focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20"
                    value={assignedUserId}
                    onChange={(e) => setAssignedUserId(e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {users.map((user) => (
                      <option key={user.pk} value={user.pk}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Due Date */}
                <div>
                  <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                    Due Date
                  </label>
                  <input
                    type="datetime-local"
                    className="tw-w-full tw-rounded-xl tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-700 tw-px-4 tw-py-3 tw-text-base dark:tw-text-white focus:tw-border-blue-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500/20"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                </div>

                {/* Actions */}
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-pt-4 tw-border-t tw-border-gray-200 dark:tw-border-gray-700">
                  <button
                    type="button"
                    onClick={onHide}
                    className="tw-px-5 tw-py-3 tw-rounded-xl tw-text-base tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700 tw-transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !title}
                    className="tw-px-6 tw-py-3 tw-rounded-xl tw-bg-blue-500 tw-text-white tw-font-semibold tw-text-base tw-shadow-lg tw-shadow-blue-500/30 hover:tw-bg-blue-600 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="tw-flex tw-items-center tw-gap-2">
                        <svg className="tw-animate-spin tw-h-4 tw-w-4" viewBox="0 0 24 24">
                          <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </span>
                    ) : (
                      "Create Ticket"
                    )}
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
